import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, ShareNetwork, BookmarkSimple, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { openInTool, openTargetFor } from "@/lib/openIn";

const ghost = "flex h-9 items-center gap-1.5 rounded-full bg-black/5 px-3.5 text-xs font-medium text-ink transition-colors hover:bg-black/10 disabled:opacity-40";

export function OutputTerminal({
  title, text, streaming = false, empty = false, loading = false,
  onShare, onSave, saved = false, testId = "output-text", compact = false, target,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Prompt copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const handleOpen = async () => {
    const t = await openInTool(target, text);
    toast.success(`Copied & opening ${t.label} — paste if it isn't filled in`);
  };

  return (
    <div className="stp-card overflow-clip rounded-3xl">
      <div data-testid="output-toolbar" className="sticky top-[calc(66px+env(safe-area-inset-top))] z-10 flex flex-wrap items-center justify-between gap-2 border-b border-black/5 bg-white/95 px-4 py-3 backdrop-blur sm:static sm:bg-transparent sm:px-5 sm:backdrop-blur-none">
        <span className="hidden items-center gap-2 text-sm font-medium text-ink sm:flex">
          <Sparkle size={16} weight="fill" className="text-brand" /> {title}
        </span>
        <div className="flex w-full items-center justify-between gap-1.5 sm:w-auto sm:flex-wrap sm:justify-start">
          {onSave && (
            <button data-testid="save-button" onClick={onSave} disabled={!text} className={ghost}>
              <BookmarkSimple size={14} weight={saved ? "fill" : "bold"} /> <span className="hidden sm:inline">{saved ? "Saved" : "Save"}</span>
            </button>
          )}
          {onShare && (
            <button data-testid="share-button" onClick={onShare} disabled={!text} className={ghost}>
              <ShareNetwork size={14} weight="bold" /> <span className="hidden sm:inline">Share</span>
            </button>
          )}
          <button data-testid="copy-button" onClick={handleCopy} disabled={!text} className={ghost}>
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />} {copied ? "Copied" : "Copy"}
          </button>
          {target && text && !streaming && (
            <button data-testid="open-in-tool-button" onClick={handleOpen} className="flex h-9 items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover">
              <span className="sm:hidden">Open</span><span className="hidden sm:inline">Open in {openTargetFor(target).label}</span> <ArrowSquareOut size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
      <div className={`relative bg-[#FAFAFC] p-5 sm:p-6 ${compact ? "min-h-[160px]" : "min-h-[220px]"}`}>
        <AnimatePresence mode="wait">
          {loading && !text ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3" data-testid="output-loading">
              <div className="stp-shimmer h-3 w-3/4 rounded-full" />
              <div className="stp-shimmer h-3 w-full rounded-full" />
              <div className="stp-shimmer h-3 w-5/6 rounded-full" />
              <p className="pt-3 text-sm text-mist">Writing your prompt…</p>
            </motion.div>
          ) : empty && !text ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex h-[170px] items-center justify-center text-center" data-testid="output-empty">
              <p className="text-sm text-mist">Your ready-to-paste prompt will appear here</p>
            </motion.div>
          ) : (
            <pre data-testid={testId} className={`whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-ink ${streaming ? "stp-caret" : ""}`}>
              {text}
            </pre>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
